import { useEffect, useRef, useState } from "react";
import { PortfolioItem } from "@/pages/Portfolio";
import { ArrowLeft, Pause, Play, Share2, Volume2, VolumeX } from "lucide-react";

interface ReelVideoProps {
  item: PortfolioItem;
  isActive: boolean;
  isMuted: boolean;
  onMutedChange: (isMuted: boolean) => void;
  onBack?: () => void;
  onShare?: () => void;
}

const ReelVideo: React.FC<ReelVideoProps> = ({
  item,
  isActive,
  isMuted,
  onMutedChange,
  onBack,
  onShare,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !isActive || isMuted;

    if (isActive) {
      video.play().catch(() => setIsPlaying(false));
      setIsPlaying(!video.paused);
    } else {
      video.pause();
      video.currentTime = 0;
      setIsPlaying(false);
    }
  }, [isActive, isMuted, item.src]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().catch(() => {});
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    onMutedChange(!isMuted);
  };

  return (
    <div className="relative aspect-[9/16] max-h-[calc(100svh-11rem)] w-full max-w-[430px] overflow-hidden rounded-lg border border-[#f5f5f5]/15 bg-black shadow-2xl">
      <video
        ref={videoRef}
        src={item.src}
        className="h-full w-full object-contain"
        loop
        muted={!isActive || isMuted}
        playsInline
        preload="metadata"
        onClick={togglePlay}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-4 lg:hidden">
        {item.brand && (
          <p className="font-poppins text-xs font-semibold uppercase tracking-[0.16em] text-[#c3f620]">
            {item.brand}
          </p>
        )}
      </div>

      {!isPlaying && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-black/60 text-[#f5f5f5]">
            <Play className="ml-1 h-8 w-8" />
          </span>
        </div>
      )}

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="absolute left-3 top-3 grid h-10 w-10 place-items-center rounded-full bg-black/55 text-[#f5f5f5] backdrop-blur outline-none ring-[#00a878] transition hover:bg-black/75 focus-visible:ring-2"
          aria-label="Back to categories"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      )}

      <div className="absolute right-3 bottom-12 lg:bottom-3 flex flex-col gap-2">
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            className="grid pr-0.5 h-10 w-10 place-items-center rounded-full bg-black/55 text-[#f5f5f5] backdrop-blur outline-none ring-[#00a878] transition hover:bg-black/75 focus-visible:ring-2"
            aria-label="Share video"
          >
            <Share2 className="h-5 w-5" />
          </button>
        )}
        <button
          type="button"
          onClick={toggleMute}
          className="grid h-10 w-10 place-items-center rounded-full bg-black/55 text-[#f5f5f5] backdrop-blur outline-none ring-[#00a878] transition hover:bg-black/75 focus-visible:ring-2"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
          {isMuted ? (
            <VolumeX className="h-5 w-5" />
          ) : (
            <Volume2 className="h-5 w-5" />
          )}
        </button>
        <button
          type="button"
          onClick={togglePlay}
          className="grid h-10 w-10 place-items-center rounded-full bg-black/55 text-[#f5f5f5] backdrop-blur outline-none ring-[#00a878] transition hover:bg-black/75 focus-visible:ring-2"
          aria-label={isPlaying ? "Pause video" : "Play video"}
        >
          {isPlaying ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="ml-0.5 h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  );
};

export default ReelVideo;
